import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Parameter, ParameterRange } from '@shared/types';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import { Minus, Plus } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers (identical to XYPadInput)
// ---------------------------------------------------------------------------

function resolveNum(v: ParameterRange['min']): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function snapToStep(value: number, step: number | undefined): number {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

function fracToValue(
  frac: number,
  min: number,
  max: number,
  step?: number,
): number {
  const raw = min + frac * (max - min);
  return snapToStep(clamp(raw, min, max), step);
}

function valueToFrac(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return clamp((value - min) / (max - min), 0, 1);
}

function fmt(n: number): string {
  return String(parseFloat(n.toPrecision(6)));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function XYZPadInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const axes = param.xyzPadAxes;

  const xMin = resolveNum(axes?.x.min) ?? 0;
  const xMax = resolveNum(axes?.x.max) ?? 100;
  const xStep = resolveNum(axes?.x.step);
  const yMin = resolveNum(axes?.y.min) ?? 0;
  const yMax = resolveNum(axes?.y.max) ?? 100;
  const yStep = resolveNum(axes?.y.step);
  const zMin = resolveNum(axes?.z.min) ?? 0;
  const zMax = resolveNum(axes?.z.max) ?? 100;
  const zStep = resolveNum(axes?.z.step);
  const xLabel = axes?.xLabel ?? 'X';
  const yLabel = axes?.yLabel ?? 'Y';
  const zLabel = axes?.zLabel ?? 'Z';

  // Current value — 3-element number array.
  const valueArr = Array.isArray(param.value)
    ? (param.value as number[])
    : [0, 0, 0];
  const [localX, setLocalX] = useState<number>(valueArr[0]);
  const [localY, setLocalY] = useState<number>(valueArr[1]);
  const [localZ, setLocalZ] = useState<number>(valueArr[2]);

  // Sync when parent pushes a new value.
  const prevValueRef = useRef(param.value);
  if (prevValueRef.current !== param.value) {
    prevValueRef.current = param.value;
    setLocalX(valueArr[0]);
    setLocalY(valueArr[1]);
    setLocalZ(valueArr[2]);
  }

  // Throttled commit (200 ms).
  const lastCommitTime = useRef(0);
  const commitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledCommit = useCallback(
    (x: number, y: number, z: number) => {
      const now = Date.now();
      const doCommit = () => {
        onCommit([x, y, z]);
        lastCommitTime.current = Date.now();
        commitTimeout.current = null;
      };
      if (now - lastCommitTime.current >= 200) {
        doCommit();
      } else {
        if (commitTimeout.current) clearTimeout(commitTimeout.current);
        commitTimeout.current = setTimeout(
          doCommit,
          200 - (now - lastCommitTime.current),
        );
      }
    },
    [onCommit],
  );

  useEffect(() => {
    return () => {
      if (commitTimeout.current) clearTimeout(commitTimeout.current);
    };
  }, []);

  const immediateCommit = useCallback(
    (x: number, y: number, z: number) => {
      if (commitTimeout.current) {
        clearTimeout(commitTimeout.current);
        commitTimeout.current = null;
      }
      onCommit([x, y, z]);
      lastCommitTime.current = Date.now();
    },
    [onCommit],
  );

  // -------------------------------------------------------------------------
  // XY pad pointer interaction
  // -------------------------------------------------------------------------
  const padRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number, immediate: boolean) => {
      const el = padRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const fracX = (clientX - rect.left) / rect.width;
      const fracY = 1 - (clientY - rect.top) / rect.height; // screen-Y inverted
      const newX = fracToValue(fracX, xMin, xMax, xStep);
      const newY = fracToValue(fracY, yMin, yMax, yStep);
      setLocalX(newX);
      setLocalY(newY);
      if (immediate) immediateCommit(newX, newY, localZ);
      else throttledCommit(newX, newY, localZ);
    },
    [
      xMin,
      xMax,
      xStep,
      yMin,
      yMax,
      yStep,
      localZ,
      throttledCommit,
      immediateCommit,
    ],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    updateFromPointer(e.clientX, e.clientY, false);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    updateFromPointer(e.clientX, e.clientY, false);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    updateFromPointer(e.clientX, e.clientY, true);
  };

  // -------------------------------------------------------------------------
  // Z slider callbacks
  // -------------------------------------------------------------------------
  const lastZCommit = useRef(0);
  const zCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledZCommit = useCallback(
    (z: number) => {
      const now = Date.now();
      const doCommit = () => {
        onCommit([localX, localY, z]);
        lastZCommit.current = Date.now();
        zCommitTimer.current = null;
      };
      if (now - lastZCommit.current >= 200) {
        doCommit();
      } else {
        if (zCommitTimer.current) clearTimeout(zCommitTimer.current);
        zCommitTimer.current = setTimeout(
          doCommit,
          200 - (now - lastZCommit.current),
        );
      }
    },
    [onCommit, localX, localY],
  );

  useEffect(() => {
    return () => {
      if (zCommitTimer.current) clearTimeout(zCommitTimer.current);
    };
  }, []);

  const handleZChange = useCallback(
    ([z]: number[]) => {
      setLocalZ(z);
      throttledZCommit(z);
    },
    [throttledZCommit],
  );

  const handleZCommit = useCallback(
    ([z]: number[]) => {
      if (zCommitTimer.current) {
        clearTimeout(zCommitTimer.current);
        zCommitTimer.current = null;
      }
      setLocalZ(z);
      onCommit([localX, localY, z]);
      lastZCommit.current = Date.now();
    },
    [onCommit, localX, localY],
  );

  // -------------------------------------------------------------------------
  // Text input blur handlers
  // -------------------------------------------------------------------------
  const handleAxisBlur = (
    axis: 'x' | 'y' | 'z',
    raw: string,
    min: number,
    max: number,
    step: number | undefined,
  ) => {
    const n = parseFloat(raw);
    if (isNaN(n)) return;
    const v = snapToStep(clamp(n, min, max), step);
    if (axis === 'x') {
      setLocalX(v);
      immediateCommit(v, localY, localZ);
    }
    if (axis === 'y') {
      setLocalY(v);
      immediateCommit(localX, v, localZ);
    }
    if (axis === 'z') {
      setLocalZ(v);
      immediateCommit(localX, localY, v);
    }
  };

  // -------------------------------------------------------------------------
  // Layout geometry
  // -------------------------------------------------------------------------
  const dotLeft = `${valueToFrac(localX, xMin, xMax) * 100}%`;
  const dotTop = `${(1 - valueToFrac(localY, yMin, yMax)) * 100}%`;

  const xSpan = Math.abs(xMax - xMin) || 1;
  const ySpan = Math.abs(yMax - yMin) || 1;
  const rawAspect = xSpan / ySpan;
  const aspect = Math.min(2.5, Math.max(0.4, rawAspect));

  const zStepVal = zStep ?? (zMax - zMin) / 100;
  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-2"
      />
      <div className="flex flex-col gap-2">
        {/* XY pad */}
        <div
          ref={padRef}
          className="relative w-full cursor-crosshair touch-none select-none rounded-md border border-border bg-muted/40"
          style={{ aspectRatio: String(aspect) }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* Grid lines */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            preserveAspectRatio="none"
          >
            {[25, 50, 75].map((pct) => (
              <React.Fragment key={pct}>
                <line
                  x1={`${pct}%`}
                  y1="0%"
                  x2={`${pct}%`}
                  y2="100%"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="1"
                />
                <line
                  x1="0%"
                  y1={`${pct}%`}
                  x2="100%"
                  y2={`${pct}%`}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="1"
                />
              </React.Fragment>
            ))}
            <line
              x1="50%"
              y1="0%"
              x2="50%"
              y2="100%"
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <line
              x1="0%"
              y1="50%"
              x2="100%"
              y2="50%"
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            {/* Axis labels inside the pad */}
            <text
              x="3"
              y="50%"
              dominantBaseline="middle"
              fontSize="8"
              fill="currentColor"
              fillOpacity="0.35"
              style={{ userSelect: 'none' }}
            >
              {xLabel}
            </text>
            <text
              x="50%"
              y="6"
              textAnchor="middle"
              fontSize="8"
              fill="currentColor"
              fillOpacity="0.35"
              style={{ userSelect: 'none' }}
            >
              {yLabel}
            </text>
          </svg>

          {/* Dot indicator */}
          <div
            className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow-sm"
            style={{ left: dotLeft, top: dotTop }}
          />
        </div>

        {/* Z slider */}
        <div className="flex flex-col gap-1">
          <span className="pl-1 text-[10px] text-muted-foreground">
            {zLabel}
          </span>
          <Slider
            min={zMin}
            max={zMax}
            step={zStepVal}
            value={[localZ]}
            defaultValue={[localZ]}
            defaultMarkerStyle="line"
            className="w-full"
            onValueChange={handleZChange}
            onValueCommit={handleZCommit}
          />
        </div>

        {/* Numeric inputs for all three axes */}
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              {
                label: xLabel,
                val: localX,
                min: xMin,
                max: xMax,
                step: xStep,
                axis: 'x' as const,
              },
              {
                label: yLabel,
                val: localY,
                min: yMin,
                max: yMax,
                step: yStep,
                axis: 'y' as const,
              },
              {
                label: zLabel,
                val: localZ,
                min: zMin,
                max: zMax,
                step: zStep,
                axis: 'z' as const,
              },
            ] as const
          ).map(({ label, val, min, max, step, axis }) => {
            const [text, setText] = (() => {
              // Each axis needs its own controlled text state, but React hooks
              // can't be called conditionally — use the axis-scoped state vars.
              if (axis === 'x')
                return [
                  fmt(localX),
                  (v: string) => {
                    const n = parseFloat(v);
                    if (!isNaN(n)) setLocalX(n);
                  },
                ] as const;
              if (axis === 'y')
                return [
                  fmt(localY),
                  (v: string) => {
                    const n = parseFloat(v);
                    if (!isNaN(n)) setLocalY(n);
                  },
                ] as const;
              return [
                fmt(localZ),
                (v: string) => {
                  const n = parseFloat(v);
                  if (!isNaN(n)) setLocalZ(n);
                },
              ] as const;
            })();
            const handleStep = (direction: 1 | -1) => {
              const s = step ?? 1;
              const next = snapToStep(clamp(Math.round((val + direction * s) / s) * s, min, max), step);
              if (axis === 'x') { setLocalX(next); immediateCommit(next, localY, localZ); }
              else if (axis === 'y') { setLocalY(next); immediateCommit(localX, next, localZ); }
              else { setLocalZ(next); immediateCommit(localX, localY, next); }
            };
            return (
              <div key={axis} className="flex flex-col gap-1">
                <span className="pl-1 text-[10px] text-muted-foreground">
                  {label}
                </span>
                <div className="flex items-center gap-1">
                  <Input
                    className="h-7 min-w-0 flex-1 px-2 text-center text-xs"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) =>
                      handleAxisBlur(axis, e.target.value, min, max, step)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                  />
                  <div className="flex flex-col">
                    <button
                      className="flex h-3.5 w-5 items-center justify-center rounded-t border border-b-0 border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                      tabIndex={-1}
                      aria-label={`Increase ${label}`}
                      disabled={val >= max}
                      onClick={() => handleStep(1)}
                    >
                      <Plus className="h-2.5 w-2.5" />
                    </button>
                    <button
                      className="flex h-3.5 w-5 items-center justify-center rounded-b border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                      tabIndex={-1}
                      aria-label={`Decrease ${label}`}
                      disabled={val <= min}
                      onClick={() => handleStep(-1)}
                    >
                      <Minus className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
